/**
 * `/v1/ops/email-notices/*` transport (#1911).
 *
 * Raw calls only — React Query wiring lives in `useEmailNotices.ts`, matching
 * the `models.ts` / `useModels.ts` split.
 *
 * Note what is NOT here: no client-side re-check of the two send gates. The
 * console shows their state so an operator is never surprised, but the refusal
 * itself is the platform route's and the migration's CHECK constraint's. A
 * third copy here would be the one that drifts, and it would drift in the
 * direction of permitting.
 */
import type {
  CreateEmailNoticeRequest,
  EmailNotice,
  EmailNoticeDetail,
  EmailNoticeRecipientPreviewResponse,
  EmailNoticeSendResponse,
  EmailNoticeStatus,
  EmailNoticeTestSendRequest,
  EmailNoticeTestSendResponse,
  FinalizeEmailNoticeRecipientsRequest,
  FinalizeEmailNoticeRecipientsResponse,
  ListEmailNoticesResponse,
  NoticeAudience,
  PreviewEmailNoticeAudienceRequest,
  UpdateEmailNoticeRequest,
} from '@nadoutong/contracts';
import { opsApiRequest } from './client.ts';

const BASE = '/v1/ops/email-notices';

function noticePath(id: string, suffix = ''): string {
  return `${BASE}/${encodeURIComponent(id)}${suffix}`;
}

export async function listEmailNotices(
  status: EmailNoticeStatus | null,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<readonly EmailNotice[]> {
  const query = status === null ? '' : `?status=${encodeURIComponent(status)}`;
  const response = await opsApiRequest<ListEmailNoticesResponse>(
    `${BASE}${query}`,
    { signal },
    fetchImpl,
  );
  return response.notices;
}

export async function getEmailNotice(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<EmailNoticeDetail> {
  return opsApiRequest<EmailNoticeDetail>(noticePath(id), { signal }, fetchImpl);
}

export async function createEmailNotice(
  body: CreateEmailNoticeRequest,
  fetchImpl: typeof fetch,
): Promise<EmailNoticeDetail> {
  return opsApiRequest<EmailNoticeDetail>(BASE, { method: 'POST', body }, fetchImpl);
}

export async function updateEmailNotice(
  id: string,
  body: UpdateEmailNoticeRequest,
  fetchImpl: typeof fetch,
): Promise<EmailNoticeDetail> {
  return opsApiRequest<EmailNoticeDetail>(noticePath(id), { method: 'PATCH', body }, fetchImpl);
}

export async function deleteEmailNotice(
  id: string,
  fetchImpl: typeof fetch,
): Promise<{ readonly deleted: boolean; readonly id: string }> {
  return opsApiRequest<{ deleted: boolean; id: string }>(
    noticePath(id),
    { method: 'DELETE' },
    fetchImpl,
  );
}

/**
 * Computes the recipient set for an audience and writes nothing (#1920).
 *
 * A POST that mutates nothing looks wrong until you ask what the alternative
 * costs: this is the call the console makes while the operator is still
 * deciding, and if looking at the list were a commitment the "see it before you
 * send it" gate would not exist.
 *
 * It takes the AUDIENCE rather than a notice id, which is what lets the console
 * answer "how many people is this" before a title and a body have been invented
 * and a draft saved. The predecessor (`POST /:id/recipients/preview`) read the
 * stored audience and discarded the rest of the row, so the id bought nothing
 * and cost the operator a save.
 */
export async function previewEmailNoticeAudience(
  audience: NoticeAudience,
  fetchImpl: typeof fetch,
): Promise<EmailNoticeRecipientPreviewResponse> {
  const body: PreviewEmailNoticeAudienceRequest = { audience };
  return opsApiRequest<EmailNoticeRecipientPreviewResponse>(
    `${BASE}/audience/preview`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function finalizeEmailNoticeRecipients(
  id: string,
  body: FinalizeEmailNoticeRecipientsRequest,
  fetchImpl: typeof fetch,
): Promise<FinalizeEmailNoticeRecipientsResponse> {
  return opsApiRequest<FinalizeEmailNoticeRecipientsResponse>(
    noticePath(id, '/recipients'),
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function testSendEmailNotice(
  id: string,
  body: EmailNoticeTestSendRequest,
  fetchImpl: typeof fetch,
): Promise<EmailNoticeTestSendResponse> {
  return opsApiRequest<EmailNoticeTestSendResponse>(
    noticePath(id, '/test-send'),
    { method: 'POST', body },
    fetchImpl,
  );
}

/**
 * `account_ids` narrows the pass to a retry subset; omitting it sends to every
 * not-yet-`sent` row. Already-delivered addresses are skipped by the primary
 * key either way, which is what makes a second press safe.
 */
export async function sendEmailNotice(
  id: string,
  body: { readonly account_ids?: readonly string[] },
  fetchImpl: typeof fetch,
): Promise<EmailNoticeSendResponse> {
  return opsApiRequest<EmailNoticeSendResponse>(
    noticePath(id, '/send'),
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function cancelEmailNotice(
  id: string,
  fetchImpl: typeof fetch,
): Promise<{ readonly notice: EmailNotice }> {
  return opsApiRequest<{ notice: EmailNotice }>(
    noticePath(id, '/cancel'),
    { method: 'POST' },
    fetchImpl,
  );
}
